import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Grid, TextField, Button,
  CircularProgress, Alert, Snackbar, Divider, Switch, FormControlLabel,
  Slider, Chip,
} from '@mui/material'
import { Save, Refresh } from '@mui/icons-material'
import { adminApi, kbApi } from '../services/api'

interface ConfigValues {
  // Severity escalation thresholds
  escalation_medium_to_high: number
  escalation_high_to_critical: number
  // Pattern proposal settings
  auto_propose_enabled: boolean
  proposal_min_confidence: number
  // Scoring weights
  scoring_critical_weight: number
  scoring_high_weight: number
  scoring_medium_weight: number
  scoring_low_weight: number
}

const DEFAULT_CONFIG: ConfigValues = {
  escalation_medium_to_high: 100,
  escalation_high_to_critical: 500,
  auto_propose_enabled: true,
  proposal_min_confidence: 0.5,
  scoring_critical_weight: 10,
  scoring_high_weight: 5,
  scoring_medium_weight: 2,
  scoring_low_weight: 1,
}

const SystemParametersPanel = () => {
  const [config, setConfig] = useState<ConfigValues>(DEFAULT_CONFIG)
  const [originalConfig, setOriginalConfig] = useState<ConfigValues>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [kbStats, setKbStats] = useState<any>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    fetchConfig()
    kbApi.getStats().then(r => setKbStats(r.data)).catch(() => {})
  }, [])

  const fetchConfig = async () => {
    setLoading(true)
    try {
      const res = await adminApi.getConfig()
      const data = res.data?.config || res.data || {}
      const merged = {
        ...DEFAULT_CONFIG,
        ...data.fine_tuning,
      }
      setConfig(merged)
      setOriginalConfig(merged)
    } catch (e) {
      // Use defaults if config service unavailable
      console.error('Failed to fetch config:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await adminApi.updateConfig('fine_tuning', config)
      setOriginalConfig(config)
      setSnackbar({ open: true, message: 'Parameters saved', severity: 'success' })
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save parameters', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig)

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>

  return (
    <Box>
      {/* Severity Escalation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Severity Escalation Thresholds</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Auto-escalate rule severity when detection counts exceed these thresholds.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Medium → High (detections)"
                value={config.escalation_medium_to_high}
                onChange={e => setConfig({ ...config, escalation_medium_to_high: parseInt(e.target.value) || 0 })}
                helperText="Escalate medium-severity rules to high after this many detections"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="High → Critical (detections)"
                value={config.escalation_high_to_critical}
                onChange={e => setConfig({ ...config, escalation_high_to_critical: parseInt(e.target.value) || 0 })}
                helperText="Escalate high-severity rules to critical after this many detections"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pattern Learning */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Self-Learning Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control how scanners propose new patterns for the knowledge base.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.auto_propose_enabled}
                onChange={e => setConfig({ ...config, auto_propose_enabled: e.target.checked })}
              />
            }
            label="Allow scanners to auto-propose new patterns"
            sx={{ mb: 2 }}
          />
          <Box sx={{ px: 1 }}>
            <Typography variant="body2" gutterBottom>
              Minimum confidence for pattern proposals: <strong>{config.proposal_min_confidence}</strong>
            </Typography>
            <Slider
              value={config.proposal_min_confidence}
              onChange={(_, v) => setConfig({ ...config, proposal_min_confidence: v as number })}
              min={0.1} max={1.0} step={0.05}
              marks={[{ value: 0.3, label: '0.3' }, { value: 0.5, label: '0.5' }, { value: 0.8, label: '0.8' }]}
              valueLabelDisplay="auto"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Scoring Weights */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Finding Severity Weights</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Weights used when calculating aggregate scores from findings.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth type="number" label="Critical" value={config.scoring_critical_weight}
                onChange={e => setConfig({ ...config, scoring_critical_weight: parseInt(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth type="number" label="High" value={config.scoring_high_weight}
                onChange={e => setConfig({ ...config, scoring_high_weight: parseInt(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth type="number" label="Medium" value={config.scoring_medium_weight}
                onChange={e => setConfig({ ...config, scoring_medium_weight: parseInt(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth type="number" label="Low" value={config.scoring_low_weight}
                onChange={e => setConfig({ ...config, scoring_low_weight: parseInt(e.target.value) || 0 })}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* KB Info (read-only) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Knowledge Base Info</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Embedding Model</Typography>
              <Typography variant="body1">all-MiniLM-L6-v2</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Dimensions</Typography>
              <Typography variant="body1">384</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Total Rules</Typography>
              <Typography variant="body1">{kbStats?.total_rules || kbStats?.total || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Active Rules</Typography>
              <Typography variant="body1">{kbStats?.active_rules || '-'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Save Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined" startIcon={<Refresh />}
          onClick={() => { setConfig(originalConfig) }}
          disabled={!hasChanges}
        >
          Reset
        </Button>
        <Button
          variant="contained" startIcon={<Save />}
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Parameters'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SystemParametersPanel
